import * as React from "react";
import { RoutingActions } from "../../../../../../react-core/Flux/Variables";
import { PartLoading } from "../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { HierarchyLevelName } from "../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { UserClaim } from "../../../../../../Wrap/Constants/Enums/UserClaim";
import { CustomerSearchModel } from "../../../../../../Wrap/Models/Models/Customer/CustomerSearch/CustomerSearchModel";
import { CustomerSearchResult } from "../../../../../../Wrap/Models/Models/Customer/CustomerSearch/CustomerSearchResult";
import { Organisation } from "../../../../../../Wrap/Models/Models/Customer/Organisation";
import { OrganisationSearchModel } from "../../../../../../Wrap/Models/Models/Customer/OrganisationSearch/OrganisationSearchModel";
import { OrganisationSearchResult } from "../../../../../../Wrap/Models/Models/Customer/OrganisationSearch/OrganisationSearchResult";
import { AccountSearchModel } from "../../../../../../Wrap/Models/Models/Transformation/Account/AccountSearchModel";
import { AccountSearchResult } from "../../../../../../Wrap/Models/Models/Transformation/Account/AccountSearchResult";
import { HierarchyIdHelper } from "../../../../../Shared/Common/HierarchyIdHelper";
import { checkPermission } from "../../../../../Shared/Permission/PermissionHelper";
import { ITableConfig, Table } from "../../../../../Shared/Table/Table";
import { Urls } from "../../../../Urls";
import { CustomerDetailsPopup } from "../../CustomerMerge/Components/CustomerDetailsPopUp";
import { accountStatusMap, clientTypeEnum, customerStatusMap, organisationStatusMap } from "../Resource/Content";
import { ClientDetailsPopup } from "../../CustomerMerge/Components/ClientDetailsPopUp";

interface IClientSearchResultTableProps {
    data: CustomerSearchModel | OrganisationSearchModel | AccountSearchModel;
    clientType: clientTypeEnum;
    onPageChange: (pageIndex, pageSize) => void;
    currentPage: number;
    isSearching: boolean;
}

export const ClientSearchResultTable = (props: IClientSearchResultTableProps) => {
    if (!props.data) {return null;}

    const col: ITableConfig[] = [
        {
            title: "Name",
            render: (data, row: CustomerSearchResult | OrganisationSearchResult | AccountSearchResult) => {
                let name = "";
                if (props.clientType === clientTypeEnum.Individual) {
                    name = `${(row as CustomerSearchResult).FirstName} ${(row as CustomerSearchResult).LastName}`;
                } else if (props.clientType === clientTypeEnum.Organisation) {
                    name = (row as OrganisationSearchResult).Name;
                } else {
                    name = (row as AccountSearchResult).AccountName;
                }

                return checkPermission(UserClaim.AdviserPortalClientSummaryCanView) ? (
                    <button
                        className="hyperlink text-left"
                        onClick={() =>
                            RoutingActions.navigateTo(
                                Urls.get()
                                    .main.PortfolioDashboard({
                                        hierarchyId:
                                            props.clientType !== clientTypeEnum.Consolidated
                                                ? HierarchyIdHelper.toHierarchyIdString(
                                                      HierarchyLevelName.Customer,
                                                      row.CustomerId
                                                  )
                                                : (row as AccountSearchResult).HierarchyId,
                                    })
                                    .summary.getUrl()
                            )
                        }
                    >
                        {name}
                    </button>
                ) : (
                    name
                );
            },
        },
        {
            title: "Date of birth",
            key: "DateOfBirth",
            render: (data, row: CustomerSearchResult) => {
                if (props.clientType !== clientTypeEnum.Individual) {return "-";}
                return (
                    <>
                    {JsonDateParser.getFormatDate(row.DateOfBirth, "dd/MM/yyyy")}
                    </>
                );
            },
        },
        {
            title: "Client type",
            key: "",
            render: (data, row) => {
                return props.clientType;
            },
        },
        {
            title: "Assigned adviser",
            key: "",
            render: (data, row: CustomerSearchResult | OrganisationSearchResult | AccountSearchResult) => {
                if (props.clientType === clientTypeEnum.Consolidated) {return (row as AccountSearchResult).AdviserName;}
                return (row as CustomerSearchResult | OrganisationSearchResult).OwnerAdviserName;
            },
        },
        {
            title: "Status",
            key: "CustomerStatus",
            render: (data, row: CustomerSearchResult | OrganisationSearchResult | AccountSearchResult) => {
                return props.clientType === clientTypeEnum.Individual
                    ? (row as CustomerSearchResult).IsProspect
                        ? "Prospect"
                        : customerStatusMap[data]
                    : props.clientType === clientTypeEnum.Organisation
                    ? (row as OrganisationSearchResult).IsProspect
                        ? "Prospect"
                        : organisationStatusMap[(row as Organisation).Status]
                    : accountStatusMap[(row as AccountSearchResult).Status];
            },
        },
        {
            title: "Details",            
            render: (data, row: CustomerSearchResult | OrganisationSearchResult | AccountSearchResult, index: number) => {
                return(
                    <ClientDetailsPopup
                        customerId = {row.CustomerId}
                        clientType = {props.clientType}
                    />           
                   );
            },
        },
    ];
    
    return (
        <PartLoading isLoading={props.isSearching}>
            <Table
                data={
                    props.clientType === clientTypeEnum.Individual
                        ? (props.data as CustomerSearchModel).CustomerList
                        : props.clientType === clientTypeEnum.Organisation
                        ? (props.data as OrganisationSearchModel).PageOfResults
                        : (props.data as AccountSearchModel).AccountList
                }
                col={col}
                showEmptyMsg
                cssWrapper="mb-60"
                needPagination={{
                    totalNumOfResult: props.data.TotalNumberOfResults,
                    onPaginationChange: (pageIndex, pageSize) => {
                        props.onPageChange(pageIndex, pageSize);
                    },
                    pageSize: 20,
                    currentPage: props.currentPage,
                }}
                rowCssWrapper={props.clientType === clientTypeEnum.Consolidated ? "pre" : null}
            />
        </PartLoading>
    );
};
